import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import inventoryCountService from '../../services/inventoryCountService';
import { getWarehouses } from '../../services/warehousesService';
import { productService } from '../../services/productService';
import Toast from '../../components/Common/Toast';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('es-PE');
}

function toDatetimeLocalValue(date) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function suggestedCampaignEnd(settings) {
  const days = Number(settings?.inventory_rotation_days || 0);
  if (!days) return '';
  const due = settings?.rotation_due_at ? new Date(settings.rotation_due_at) : new Date(Date.now() + days * 86400000);
  return toDatetimeLocalValue(due);
}

export default function InventoryCountPanel({ warehouseId, mode = 'warehouse' }) {
  const { kardexAuditModuleEnabled } = useModuleSettings();
  const [warehouse, setWarehouse] = useState(null);
  const [settings, setSettings] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [responsibleUsers, setResponsibleUsers] = useState([]);
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedProductLabels, setSelectedProductLabels] = useState({});
  const [lineForm, setLineForm] = useState({ product_id: '', physical_qty: '' });
  const [newCampaign, setNewCampaign] = useState({
    type: 'general',
    name: '',
    required_product_count: 10,
    starts_at: '',
    ends_at: '',
  });
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  const isAuditMode = mode === 'audit';

  const load = useCallback(async () => {
    if (!warehouseId || !kardexAuditModuleEnabled) return;
    setLoading(true);
    try {
      const [whRes, settingsRes, campaignsRes] = await Promise.all([
        getWarehouses({ active_only: true }),
        inventoryCountService.getSettings(warehouseId),
        inventoryCountService.listCampaigns(warehouseId),
      ]);
      const wh = (whRes.data || []).find((w) => String(w.id) === String(warehouseId));
      setWarehouse(wh || null);
      if (settingsRes.success) setSettings(settingsRes.data);
      if (campaignsRes.success) {
        setCampaigns(campaignsRes.data || []);
        setResponsibleUsers(campaignsRes.responsible_users || []);
      }
    } catch {
      setToast({ isOpen: true, message: 'Error al cargar toma de inventario', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [warehouseId, kardexAuditModuleEnabled]);

  useEffect(() => {
    load();
  }, [load]);

  const saveSettings = async (patch) => {
    setSavingSettings(true);
    try {
      const res = await inventoryCountService.updateSettings(warehouseId, patch);
      if (res.success) {
        setSettings(res.data);
        setToast({ isOpen: true, message: 'Configuración guardada', type: 'success' });
      }
    } catch {
      setToast({ isOpen: true, message: 'No se pudo guardar la configuración', type: 'error' });
    } finally {
      setSavingSettings(false);
    }
  };

  const openChecklist = async (campaign) => {
    setActiveCampaign(campaign);
    try {
      const res = await inventoryCountService.getChecklist(warehouseId, campaign.id);
      if (res.success) setChecklist(res.data);
    } catch {
      setToast({ isOpen: true, message: 'Error al cargar checklist', type: 'error' });
    }
  };

  const createCampaign = async () => {
    if (!newCampaign.name.trim()) {
      setToast({ isOpen: true, message: 'Indique un nombre para la campaña', type: 'warning' });
      return;
    }
    if (!newCampaign.ends_at) {
      setToast({ isOpen: true, message: 'Indique la fecha límite de la toma de inventario', type: 'warning' });
      return;
    }
    try {
      const payload = {
        ...newCampaign,
        required_product_count: newCampaign.type === 'compra' && selectedProducts.length > 0
          ? selectedProducts.length
          : newCampaign.required_product_count,
      };
      const res = await inventoryCountService.createCampaign(warehouseId, payload);
      if (res.success) {
        if (newCampaign.type === 'compra' && selectedProducts.length > 0) {
          await inventoryCountService.syncCampaignProducts(warehouseId, res.data.id, selectedProducts);
        }
        setToast({ isOpen: true, message: 'Campaña creada', type: 'success' });
        setNewCampaign({
          type: 'general',
          name: '',
          required_product_count: 10,
          starts_at: '',
          ends_at: '',
        });
        setSelectedProducts([]);
        setSelectedProductLabels({});
        load();
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'No se pudo crear la campaña',
        type: 'error',
      });
    }
  };

  const saveLine = async () => {
    if (!activeCampaign || !lineForm.product_id) return;
    try {
      await inventoryCountService.saveLine(warehouseId, activeCampaign.id, {
        product_id: Number(lineForm.product_id),
        physical_qty: Number(lineForm.physical_qty),
        status: 'confirmed',
      });
      setToast({ isOpen: true, message: 'Conteo guardado', type: 'success' });
      openChecklist(activeCampaign);
      setLineForm({ product_id: '', physical_qty: '' });
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al guardar conteo',
        type: 'error',
      });
    }
  };

  const searchProducts = async () => {
    if (!productSearch.trim()) return;
    const res = await productService.searchForForm(productSearch.trim(), warehouseId);
    if (res.success) setProductResults(res.data || []);
  };

  const addProduct = (product) => {
    if (selectedProducts.includes(product.id)) return;
    setSelectedProducts((ids) => [...ids, product.id]);
    setSelectedProductLabels((labels) => ({
      ...labels,
      [product.id]: `${product.sku || product.id} — ${product.name}`,
    }));
    setNewCampaign((c) => ({
      ...c,
      required_product_count: selectedProducts.length + 1,
    }));
  };

  const removeProduct = (productId) => {
    setSelectedProducts((ids) => ids.filter((id) => id !== productId));
    setSelectedProductLabels((labels) => {
      const next = { ...labels };
      delete next[productId];
      return next;
    });
  };

  if (!kardexAuditModuleEnabled) {
    return (
      <div className="p-6">
        <p className="text-gray-600">El módulo de auditoría no está habilitado.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="p-6 text-gray-500">Cargando…</div>;
  }

  const progress = checklist?.progress;

  return (
    <div className={isAuditMode ? '' : 'p-6 max-w-6xl mx-auto'}>
      {!isAuditMode && (
        <nav className="text-sm text-gray-600 mb-3">
          <Link to="/warehouses" className="hover:text-teal-600">Almacenes</Link>
          <span className="mx-2">/</span>
          <Link to={`/warehouses/${warehouseId}/documents`} className="hover:text-teal-600">
            {warehouse?.name || 'Bodega'}
          </Link>
          <span className="mx-2">/</span>
          <span className="text-gray-800">Toma de inventario</span>
        </nav>
      )}

      <h1 className="text-2xl font-bold text-gray-800 mb-2">
        Toma de inventario {warehouse?.name ? `— ${warehouse.name}` : ''}
      </h1>
      <p className="text-gray-600 mb-6 text-sm">
        Configure plazos, productos y campañas por almacén. Solo registra y alerta; no genera guías GA automáticamente.
      </p>

      <section className="mb-8 p-4 bg-white border rounded-lg">
        <h2 className="font-semibold mb-3">Configuración del almacén</h2>
        <label className="flex items-center gap-2 text-sm mb-4">
          <input
            type="checkbox"
            checked={Boolean(settings?.inventory_count_enabled)}
            onChange={(e) => saveSettings({ inventory_count_enabled: e.target.checked })}
            disabled={savingSettings}
          />
          Habilitar toma de inventario en esta bodega
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <label>
            Rotación de inventario (días)
            <input
              type="number"
              min={1}
              max={365}
              className="mt-1 w-full border rounded px-2 py-2"
              placeholder="Ej. 30"
              value={settings?.inventory_rotation_days ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                setSettings((s) => ({
                  ...s,
                  inventory_rotation_days: raw === '' ? null : Number(raw),
                }));
              }}
              onBlur={(e) => {
                const raw = e.target.value;
                saveSettings({
                  inventory_rotation_days: raw === '' ? null : Number(raw),
                });
              }}
            />
            <span className="text-xs text-gray-500 mt-1 block">
              Cada cuántos días debe repetirse la toma de inventario en esta bodega.
            </span>
          </label>
          <div className="rounded border bg-gray-50 px-3 py-2 text-xs text-gray-600">
            {settings?.inventory_rotation_days ? (
              <>
                <p>
                  <strong>Próxima rotación sugerida:</strong>{' '}
                  {settings.rotation_due_at ? formatDateTime(settings.rotation_due_at) : 'Al crear la primera campaña'}
                </p>
                {settings.rotation_overdue && (
                  <p className="text-amber-800 mt-1">La rotación está vencida. Cree y active una nueva campaña.</p>
                )}
                {settings.last_closed_campaign_at && (
                  <p className="mt-1">Última campaña cerrada: {formatDateTime(settings.last_closed_campaign_at)}</p>
                )}
              </>
            ) : (
              <p>Defina la rotación en días para calcular cuándo debe repetirse el inventario.</p>
            )}
          </div>
          <label>
            Días antes del cierre para alertar
            <input
              type="number"
              min={0}
              max={30}
              className="mt-1 w-full border rounded px-2 py-2"
              value={settings?.alert_days_before_end ?? 2}
              onChange={(e) => setSettings((s) => ({ ...s, alert_days_before_end: Number(e.target.value) }))}
              onBlur={(e) => saveSettings({ alert_days_before_end: Number(e.target.value) })}
            />
            <span className="text-xs text-gray-500 mt-1 block">Cuántos días antes del fin de campaña se envía alerta de meta pendiente.</span>
          </label>
          <label>
            Horas de recordatorio (campaña compra)
            <input
              type="number"
              min={1}
              max={168}
              className="mt-1 w-full border rounded px-2 py-2"
              value={settings?.purchase_reminder_hours ?? 24}
              onChange={(e) => setSettings((s) => ({ ...s, purchase_reminder_hours: Number(e.target.value) }))}
              onBlur={(e) => saveSettings({ purchase_reminder_hours: Number(e.target.value) })}
            />
            <span className="text-xs text-gray-500 mt-1 block">Horas antes del cierre para recordar productos pendientes en lista manual.</span>
          </label>
        </div>
      </section>

      <section className="mb-8 p-4 bg-white border rounded-lg">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <h2 className="font-semibold">Responsables de la toma de inventario</h2>
          {isAuditMode && (
            <Link to="/audit/recipients" className="text-sm text-teal-700 hover:underline">
              Editar destinatarios →
            </Link>
          )}
        </div>
        {responsibleUsers.length === 0 ? (
          <p className="text-sm text-gray-500">
            No hay usuarios asignados con «Alertas inventario». Configúrelos en Auditoría → Destinatarios.
          </p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {responsibleUsers.map((u) => (
              <li
                key={u.user_id}
                className="px-3 py-1.5 bg-teal-50 text-teal-900 rounded-full text-sm border border-teal-100"
              >
                {u.name || u.email}
                {u.email && u.name ? <span className="text-teal-700/70 text-xs ml-1">({u.email})</span> : null}
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-gray-500 mt-3">
          Estos usuarios realizan el conteo en{' '}
          <Link to="/audit/my-inventory-counts" className="text-teal-700 hover:underline">Mis tomas de inventario</Link>.
        </p>
      </section>

      <section className="mb-8 p-4 bg-white border rounded-lg">
        <h2 className="font-semibold mb-1">Nueva campaña</h2>
        <p className="text-sm text-gray-500 mb-4">
          Defina el plazo y los productos. Tipo <strong>General</strong>: meta de cantidad libre. Tipo <strong>Compra</strong>: lista manual de productos.
          {settings?.inventory_rotation_days ? (
            <span className="block mt-1">
              La fecha límite sugerida usa la rotación de {settings.inventory_rotation_days} días.
            </span>
          ) : null}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <label>
            Tipo
            <select
              className="mt-1 w-full border rounded px-2 py-2"
              value={newCampaign.type}
              onChange={(e) => setNewCampaign((c) => ({ ...c, type: e.target.value }))}
            >
              <option value="general">General (meta de productos)</option>
              <option value="compra">Compra (lista manual de productos)</option>
            </select>
          </label>
          <label>
            Nombre de campaña
            <input
              className="mt-1 w-full border rounded px-2 py-2"
              placeholder="Ej. Inventario marzo 2026"
              value={newCampaign.name}
              onChange={(e) => setNewCampaign((c) => ({ ...c, name: e.target.value }))}
            />
          </label>
          {newCampaign.type === 'general' && (
            <label>
              Meta de productos a contar
              <input
                type="number"
                min={1}
                className="mt-1 w-full border rounded px-2 py-2"
                value={newCampaign.required_product_count}
                onChange={(e) => setNewCampaign((c) => ({ ...c, required_product_count: Number(e.target.value) }))}
              />
            </label>
          )}
          <label>
            Inicio (opcional)
            <input
              type="datetime-local"
              className="mt-1 w-full border rounded px-2 py-2"
              value={newCampaign.starts_at}
              onChange={(e) => setNewCampaign((c) => ({ ...c, starts_at: e.target.value }))}
            />
          </label>
          <label>
            Fecha límite de toma
            <input
              type="datetime-local"
              className="mt-1 w-full border rounded px-2 py-2"
              value={newCampaign.ends_at}
              onChange={(e) => setNewCampaign((c) => ({ ...c, ends_at: e.target.value }))}
            />
            {!newCampaign.ends_at && settings?.inventory_rotation_days && (
              <button
                type="button"
                className="mt-1 text-xs text-teal-700 hover:underline"
                onClick={() => setNewCampaign((c) => ({ ...c, ends_at: suggestedCampaignEnd(settings) }))}
              >
                Usar fecha según rotación ({settings.inventory_rotation_days} días)
              </button>
            )}
          </label>
        </div>

        {newCampaign.type === 'compra' && (
          <div className="mt-4 text-sm border-t pt-4">
            <p className="font-medium text-gray-800 mb-2">Productos asignados a esta campaña</p>
            <div className="flex gap-2 mb-2">
              <input
                className="border rounded px-2 py-2 flex-1"
                placeholder="Buscar por SKU o nombre"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchProducts()}
              />
              <button type="button" onClick={searchProducts} className="px-3 py-2 bg-gray-100 rounded hover:bg-gray-200">
                Buscar
              </button>
            </div>
            <div className="max-h-32 overflow-y-auto border rounded p-2 mb-3">
              {productResults.length === 0 ? (
                <p className="text-gray-500 text-xs px-2 py-1">Busque y agregue productos del catálogo.</p>
              ) : (
                productResults.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="block w-full text-left px-2 py-1 hover:bg-gray-50 rounded"
                    onClick={() => addProduct(p)}
                  >
                    {p.sku} — {p.name}
                  </button>
                ))
              )}
            </div>
            {selectedProducts.length > 0 ? (
              <ul className="space-y-1 border rounded p-2 bg-gray-50">
                {selectedProducts.map((id) => (
                  <li key={id} className="flex items-center justify-between gap-2 text-sm">
                    <span>{selectedProductLabels[id] || `Producto #${id}`}</span>
                    <button type="button" className="text-red-600 text-xs" onClick={() => removeProduct(id)}>
                      Quitar
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 text-xs">Agregue al menos un producto para campañas de compra.</p>
            )}
            <p className="mt-2 text-gray-600">Total asignados: {selectedProducts.length}</p>
          </div>
        )}

        <button
          type="button"
          onClick={createCampaign}
          className="mt-4 px-4 py-2 bg-teal-600 text-white rounded text-sm hover:bg-teal-700"
        >
          Crear campaña (borrador)
        </button>
      </section>

      <section className="mb-8">
        <h2 className="font-semibold mb-3">Campañas del almacén</h2>
        {campaigns.length === 0 ? (
          <p className="text-sm text-gray-500 p-4 bg-white border rounded-lg">No hay campañas creadas para este almacén.</p>
        ) : (
          <div className="space-y-2">
            {campaigns.map((c) => (
              <div key={c.id} className="p-4 bg-white border rounded-lg flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-medium">
                    {c.name}
                    <span className="text-gray-500 text-sm font-normal ml-2">
                      ({c.type === 'compra' ? 'Lista manual' : 'General'})
                    </span>
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    Progreso: {c.progress_label || `${c.counted_products || 0}/${c.required_product_count}`} · {c.status}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Inicio: {formatDateTime(c.starts_at)} · Límite: {formatDateTime(c.ends_at)}
                  </p>
                  {c.status === 'active' && responsibleUsers.length > 0 && (
                    <p className="text-xs text-teal-800 mt-2">
                      Responsables: {responsibleUsers.map((u) => u.name || u.email).join(', ')}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap items-start">
                  {c.status === 'active' && (
                    <Link
                      to={`/audit/my-inventory-counts/${warehouseId}/${c.id}`}
                      className="text-sm px-3 py-1 bg-teal-600 text-white rounded hover:bg-teal-700"
                    >
                      Vista operador
                    </Link>
                  )}
                  {c.status === 'draft' && (
                    <button
                      type="button"
                      className="text-sm px-3 py-1 bg-teal-50 text-teal-800 rounded"
                      onClick={async () => {
                        await inventoryCountService.activateCampaign(warehouseId, c.id);
                        load();
                      }}
                    >
                      Activar
                    </button>
                  )}
                  {c.status === 'active' && (
                    <>
                      <button type="button" className="text-sm px-3 py-1 bg-gray-100 rounded" onClick={() => openChecklist(c)}>
                        Checklist
                      </button>
                      <button
                        type="button"
                        className="text-sm px-3 py-1 bg-amber-50 text-amber-800 rounded"
                        onClick={async () => {
                          await inventoryCountService.closeCampaign(warehouseId, c.id);
                          setActiveCampaign(null);
                          setChecklist(null);
                          load();
                        }}
                      >
                        Cerrar
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {activeCampaign && checklist && (
        <section className="p-4 bg-white border rounded-lg">
          <h2 className="font-semibold mb-2">Checklist — {activeCampaign.name}</h2>
          {progress && (
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-1">Progreso: {progress.counted} / {progress.required}</p>
              <div className="h-2 bg-gray-200 rounded">
                <div
                  className="h-2 bg-teal-600 rounded"
                  style={{ width: `${Math.min(100, (progress.counted / Math.max(1, progress.required)) * 100)}%` }}
                />
              </div>
            </div>
          )}

          {activeCampaign.type === 'general' && (
            <div className="flex flex-wrap gap-2 mb-4 text-sm">
              <input
                className="border rounded px-2 py-1"
                placeholder="ID producto"
                value={lineForm.product_id}
                onChange={(e) => setLineForm((f) => ({ ...f, product_id: e.target.value }))}
              />
              <input
                className="border rounded px-2 py-1"
                placeholder="Cantidad física"
                value={lineForm.physical_qty}
                onChange={(e) => setLineForm((f) => ({ ...f, physical_qty: e.target.value }))}
              />
              <button type="button" onClick={saveLine} className="px-3 py-1 bg-teal-600 text-white rounded">
                Registrar
              </button>
            </div>
          )}

          <ul className="text-sm divide-y">
            {(checklist.items || []).map((item) => (
              <li key={item.product_id} className="py-2 flex justify-between items-center">
                <span>{item.product?.name || item.product_id} {item.counted ? '✓' : ''}</span>
                {activeCampaign.type === 'compra' && !item.counted && (
                  <button
                    type="button"
                    className="text-teal-700"
                    onClick={() => setLineForm({ product_id: String(item.product_id), physical_qty: '' })}
                  >
                    Contar
                  </button>
                )}
              </li>
            ))}
          </ul>

          {lineForm.product_id && (
            <div className="mt-4 flex gap-2 text-sm">
              <input
                className="border rounded px-2 py-1"
                placeholder="Cantidad física"
                value={lineForm.physical_qty}
                onChange={(e) => setLineForm((f) => ({ ...f, physical_qty: e.target.value }))}
              />
              <button type="button" onClick={saveLine} className="px-3 py-1 bg-teal-600 text-white rounded">
                Guardar conteo
              </button>
            </div>
          )}

          <p className="mt-4 text-xs text-gray-500">
            <Link to={`/warehouses/${warehouseId}/documents`} className="text-teal-600 hover:underline">
              Ir a guías de ajuste
            </Link>
            {' '}si necesita crear GA manualmente.
          </p>
        </section>
      )}

      <Toast {...toast} onClose={() => setToast((t) => ({ ...t, isOpen: false }))} />
    </div>
  );
}
